import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import SubTabs from './SubTabs';

type InventorySummary = Array<{
  product_id: number;
  name: string;
  sku: string;
  category: string | null;
  unit_of_measure: string | null;
  on_hand: number;
  reorder_point: number | null;
  reorder_quantity: number | null;
}>;

type RoastYieldRow = Array<{
  id: number;
  roast_date: string;
  product_name: string;
  green_weight: number;
  roasted_weight: number;
  shrinkage: number;
}>;

const DashboardPage = () => {
  const { data: inventory } = useQuery({
    queryKey: ['inventory-summary'],
    queryFn: () => api.get<InventorySummary>('/api/inventory/summary')
  });

  const { data: roastYield } = useQuery({
    queryKey: ['report-roast-yield'],
    queryFn: () => api.get<RoastYieldRow>('/api/reports/roast-yield')
  });

  const lowStock = (inventory ?? []).filter(
    (item) => item.reorder_point !== null && item.on_hand <= (item.reorder_point ?? 0)
  );

  const [activeTab, setActiveTab] = useState<'low-stock' | 'recent-roast' | 'overview'>('low-stock');
  const [yieldSortKey, setYieldSortKey] = useState<'id' | 'date' | 'product' | 'green' | 'roasted' | 'shrinkage'>('date');
  const [yieldSortDir, setYieldSortDir] = useState<'asc' | 'desc'>('desc');

  const sortedRoastYield = useMemo(() => {
    const rows = [...(roastYield ?? [])];
    const dir = yieldSortDir === 'asc' ? 1 : -1;
    return rows.sort((a, b) => {
      switch (yieldSortKey) {
        case 'id':
          return (a.id - b.id) * dir;
        case 'date':
          return (new Date(a.roast_date).getTime() - new Date(b.roast_date).getTime()) * dir;
        case 'product':
          return a.product_name.localeCompare(b.product_name) * dir;
        case 'green':
          return (a.green_weight - b.green_weight) * dir;
        case 'roasted':
          return (a.roasted_weight - b.roasted_weight) * dir;
        case 'shrinkage':
          return (a.shrinkage - b.shrinkage) * dir;
      }
    });
  }, [roastYield, yieldSortKey, yieldSortDir]);

  return (
    <div>
      <h2>Dashboard</h2>
      <p style={{ color: '#64748b' }}>
        Overview of your roastery operations with quick insight into stock levels, roast
        yield and open actions.
      </p>

      <SubTabs
        activeTab={activeTab}
        onChange={setActiveTab}
        tabs={[
          { id: 'low-stock', label: 'Low Stock', badge: lowStock.length },
          { id: 'recent-roast', label: 'Recent Roast Yield', badge: roastYield?.length ?? 0 },
          { id: 'overview', label: 'Overview' }
        ]}
      />

      {activeTab === 'low-stock' ? (
        <section style={{ marginTop: 8 }}>
          <h3>Low Stock Watchlist</h3>
          <div className="table-wrapper">
            {lowStock.length === 0 ? (
              <div className="empty-state">All products are above their reorder point.</div>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>SKU</th>
                    <th>Product</th>
                    <th>On Hand</th>
                    <th>Reorder Point</th>
                    <th>Suggested Order Qty</th>
                  </tr>
                </thead>
                <tbody>
                  {lowStock.map((item) => (
                    <tr key={item.product_id}>
                      <td>{item.sku}</td>
                      <td>{item.name}</td>
                      <td>{item.on_hand.toFixed(2)}</td>
                      <td>{item.reorder_point ?? '—'}</td>
                      <td>{item.reorder_quantity ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>
      ) : null}

      {activeTab === 'recent-roast' ? (
        <section style={{ marginTop: 8 }}>
          <h3>Recent Roast Yield</h3>
          <div className="table-wrapper">
            {roastYield && roastYield.length > 0 ? (
              <table>
                <thead>
                  <tr>
                    <th
                      style={{ cursor: 'pointer' }}
                      onClick={() => {
                        if (yieldSortKey === 'id') setYieldSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
                        setYieldSortKey('id');
                      }}
                    >
                      ID{yieldSortKey === 'id' ? (yieldSortDir === 'asc' ? ' ^' : ' v') : ''}
                    </th>
                    <th
                      style={{ cursor: 'pointer' }}
                      onClick={() => {
                        if (yieldSortKey === 'date') setYieldSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
                        setYieldSortKey('date');
                      }}
                    >
                      Date{yieldSortKey === 'date' ? (yieldSortDir === 'asc' ? ' ^' : ' v') : ''}
                    </th>
                    <th
                      style={{ cursor: 'pointer' }}
                      onClick={() => {
                        if (yieldSortKey === 'product') setYieldSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
                        setYieldSortKey('product');
                      }}
                    >
                      Product{yieldSortKey === 'product' ? (yieldSortDir === 'asc' ? ' ^' : ' v') : ''}
                    </th>
                    <th
                      style={{ cursor: 'pointer' }}
                      onClick={() => {
                        if (yieldSortKey === 'green') setYieldSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
                        setYieldSortKey('green');
                      }}
                    >
                      Green (kg){yieldSortKey === 'green' ? (yieldSortDir === 'asc' ? ' ^' : ' v') : ''}
                    </th>
                    <th
                      style={{ cursor: 'pointer' }}
                      onClick={() => {
                        if (yieldSortKey === 'roasted') setYieldSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
                        setYieldSortKey('roasted');
                      }}
                    >
                      Roasted (kg){yieldSortKey === 'roasted' ? (yieldSortDir === 'asc' ? ' ^' : ' v') : ''}
                    </th>
                    <th
                      style={{ cursor: 'pointer' }}
                      onClick={() => {
                        if (yieldSortKey === 'shrinkage') setYieldSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
                        setYieldSortKey('shrinkage');
                      }}
                    >
                      Shrinkage %{yieldSortKey === 'shrinkage' ? (yieldSortDir === 'asc' ? ' ^' : ' v') : ''}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedRoastYield.map((row) => (
                    <tr key={row.id}>
                      <td>{row.id}</td>
                      <td>{new Date(row.roast_date).toLocaleDateString()}</td>
                      <td>{row.product_name}</td>
                      <td>{row.green_weight.toFixed(2)}</td>
                      <td>{row.roasted_weight.toFixed(2)}</td>
                      <td>{row.shrinkage.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="empty-state">No roast batches have been recorded yet.</div>
            )}
          </div>
        </section>
      ) : null}

      {activeTab === 'overview' ? (
        <div className="cards-grid" style={{ marginTop: 16 }}>
          <div className="card">
            <h3>Inventory Items</h3>
            <div className="metrics">
              <span className="metric-label">Active SKUs</span>
              <span className="metric-value">{inventory?.length ?? 0}</span>
            </div>
            <p style={{ color: '#64748b', marginTop: 16 }}>
              Total active products tracked in Cloudflare D1 with live inventory movements.
            </p>
          </div>
          <div className="card">
            <h3>Low Stock Alerts</h3>
            <div className="metrics">
              <span className="metric-label">Items below reorder point</span>
              <span className="metric-value">{lowStock.length}</span>
            </div>
            <p style={{ color: '#64748b', marginTop: 16 }}>
              Configure reorder points per SKU to automatically surface restock actions.
            </p>
          </div>
          <div className="card">
            <h3>Latest Roast Batch</h3>
            {roastYield && roastYield.length > 0 ? (
              <div className="metrics">
                <span className="metric-label">{roastYield[0].product_name}</span>
                <span className="metric-value">{roastYield[0].roasted_weight.toFixed(2)} kg</span>
                <span className="metric-label">
                  Shrinkage: {roastYield[0].shrinkage?.toFixed(2)}%
                </span>
              </div>
            ) : (
              <p className="metric-label">No roasts recorded yet.</p>
            )}
            <p style={{ color: '#64748b', marginTop: 16 }}>
              Roast metrics are stored in D1 and roast profiles are archived in R2 for
              traceability.
            </p>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default DashboardPage;
